import { ILinkService, LinkParameters, IAppManifest } from '@metaos/hub-sdk';
import { getOrigin } from 'owa-url';
import { trace } from 'owa-trace';
import { getAppDefinition } from 'owa-app-definitions/lib/appDefinitions';
import { convertAppDefinitionToManifest } from 'owa-app-definitions/lib/convertAppDefinitionToManifest';

interface ParsedLinkParameters {
    appId: string;
    entityId?: string;
    subEntityId?: string;
    subEntityLabel?: string;
    subEntityWebUrl?: string;
}

export interface LinkNavigationParameters extends Omit<ParsedLinkParameters, 'appId'> {
    appId?: string;
    appManifest: IAppManifest;
}

interface DeepLinkHandlers {
    openLink: {
        onExecute: (params: LinkNavigationParameters) => any;
    };
    shareLink: {
        onOpen: (url: string) => any;
    };
}

interface AppHostStateContext {
    get: () => {
        appId?: string;
        entityId?: string;
    };
}

enum UrlFormatType {
    Teams = 'teams',
    Owa = 'owa',
}

type DeepLinkUrl = {
    url: URL;
    type: UrlFormatType;
};

type OwaUrl = DeepLinkUrl & { type: UrlFormatType.Owa };
type TeamsUrl = DeepLinkUrl & { type: UrlFormatType.Teams };

const URL_FORMAT_PARSER = {
    [UrlFormatType.Owa]: parseOwaUrlFormat,
    [UrlFormatType.Teams]: parseTeamsUrlFormat,
};

export default class DeepLinkService implements ILinkService {
    private readonly handlers: DeepLinkHandlers;
    private readonly context: AppHostStateContext;

    constructor(handlers: DeepLinkHandlers, context: AppHostStateContext) {
        this.handlers = handlers;
        this.context = context;
    }

    openLink(url: string): Promise<void> {
        if (!isUrlValid(url)) {
            // Invalid url: silently fail
            return Promise.resolve();
        }
        const linkUrl: DeepLinkUrl | undefined = mapUrlFormat(url);
        if (!linkUrl) {
            // Unknown url format: silently fail
            return Promise.resolve();
        }
        const parser = URL_FORMAT_PARSER[linkUrl.type];
        const navigationParams: ParsedLinkParameters | undefined = parser(
            linkUrl as OwaUrl & TeamsUrl
        );

        if (!navigationParams) {
            // Invalid pathname + QSPs: silently fail
            return Promise.resolve();
        }

        // TODO: This async should be replaced with dispatchOpenMetaOsApp
        // as the behaviors below (validation/conversion/fallback)
        // should be centralized and available elsewhere
        getAppDefinition(navigationParams.appId).then(appDefinition => {
            if (!appDefinition) {
                this._fallbackBehavior(navigationParams);
                return false;
            }

            const isEntityIdValid = appDefinition.staticTabs
                ?.map(tab => tab.entityId)
                .includes(navigationParams.entityId);
            if (!!navigationParams.entityId && !isEntityIdValid) {
                this._fallbackBehavior(navigationParams);
                return false;
            }

            this.handlers.openLink.onExecute({
                appManifest: convertAppDefinitionToManifest(appDefinition),
                ...navigationParams,
            });
            return true;
        });

        return Promise.resolve();
    }

    _fallbackBehavior({ subEntityWebUrl }: { subEntityWebUrl?: string } = {}) {
        if (subEntityWebUrl) {
            window.open(subEntityWebUrl, '_blank');
        }
    }

    shareLink(params: LinkParameters): Promise<void> {
        const { appId, entityId } = this.context.get();
        if (
            !appId ||
            !entityId ||
            !params.subEntityLabel ||
            !params.subEntityId ||
            (params.subEntityWebUrl && !isUrlValid(params.subEntityWebUrl))
        ) {
            return Promise.resolve();
        }

        const url = generateUrl(appId, entityId, params.subEntityId);
        const qsp = new URLSearchParams();

        qsp.append('label', params.subEntityLabel);
        if (params.subEntityWebUrl) {
            qsp.append('webUrl', params.subEntityWebUrl);
        }
        url.search = qsp.toString();

        this.handlers.shareLink.onOpen(url.toString());
        return Promise.resolve();
    }
}

export function generateUrl(appId: string, entityId: string, subEntityId: string): URL {
    const url = new URL(getOrigin());
    url.pathname = `${appId}/${entityId}/${subEntityId}`;
    return url;
}

export function isUrlValid(url: string | undefined): boolean {
    try {
        const inspectUrl = new URL(url as string);
        const validatePredicates = [!!inspectUrl.origin, inspectUrl.protocol === 'https:'];
        return validatePredicates.every(x => x);
    } catch {
        return false;
    }
}

export function mapUrlFormat(url: string): DeepLinkUrl | undefined {
    const parsedUrl = new URL(url);
    const knownDomains = {
        [UrlFormatType.Teams]: ['teams.microsoft.com'],
        [UrlFormatType.Owa]: [
            'outlook.office.com',
            'outlook-sdf.office.com',
            'outlook.office365.com',
            'outlook-sdf.office365.com',
            'outlook.live.com',
            'outlook-sdf.live.com',
        ],
    };
    for (let [type, domains] of Object.entries(knownDomains)) {
        if (domains.find((domain: string) => domain === parsedUrl.hostname)) {
            return {
                type,
                url: parsedUrl,
            };
        }
    }
    return undefined;
}

export function parseTeamsUrlFormat(linkUrl: TeamsUrl): ParsedLinkParameters | undefined {
    try {
        const [_a, _b, _c, appId, entityId] = linkUrl.url.pathname.split(
            '/'
        ); /* ['', 'l', 'entity OR app', '${appId}', '${entityId}'] */
        if (_a !== '' || _b !== 'l' || (_c !== 'entity' && _c !== 'app')) {
            throw new Error('Invalid Teams url format');
        }

        if (!appId) {
            throw new Error('Invalid appId');
        }
        const searchParams = new URLSearchParams(linkUrl.url.search);
        // Optional fields from QSP
        const subEntityLabel = searchParams.get('label') ?? undefined;
        const subEntityId = searchParams.has('context')
            ? JSON.parse(searchParams.get('context') as string).subEntityId ?? undefined
            : undefined;
        const subEntityWebUrl = searchParams.get('webUrl') ?? undefined;

        return {
            appId,
            ...{
                entityId,
                subEntityLabel,
                subEntityWebUrl,
                subEntityId,
            },
        };
    } catch {
        return undefined;
    }
}

export function parseOwaUrlFormat(linkUrl: OwaUrl): ParsedLinkParameters | undefined {
    try {
        const [_a, _b, appId, entityId, subEntityId] = linkUrl.url.pathname.split(
            '/'
        ); /* ['', 'host', '${appId}', '${entityId}', '$subEntityId'] */
        if (_a !== '' || _b !== 'host') {
            throw new Error('Invalid OWA url format');
        }
        if (!appId) {
            throw new Error('Invalid appId');
        }
        const searchParams = new URLSearchParams(linkUrl.url.search);
        // Optional fields from QSP
        const subEntityLabel = searchParams.get('label') ?? undefined;
        const subEntityWebUrl = searchParams.get('webUrl') ?? undefined;

        return {
            appId,
            entityId,
            subEntityLabel,
            ...{
                subEntityWebUrl,
                subEntityId,
            },
        };
    } catch (err) {
        trace.warn(`[DeepLinkService Error] ${err.message}`);
        return undefined;
    }
}
